import React, { useState, useEffect } from 'react';
import SignupRequestView from '../llac/SignupRequestView';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

export default function CalendarBookingIntentView({ intentCode, refId = null }) {
  const [state, setState] = useState('loading');
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchConfig() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/marketing/intent-booking-config/${intentCode}`, {
          credentials: 'include',
        });
        if (cancelled) { return; }

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (res.status === 410 && data.error === 'INTENT_EXPIRED') {
            setState('expired');
          } else if (res.status === 410 && data.error === 'INTENT_EXHAUSTED') {
            setState('exhausted');
          } else if (res.status === 404) {
            setState('not_found');
          } else {
            setState('error');
          }
          return;
        }

        const data = await res.json();
        if (cancelled) { return; }
        setConfig(data);
        setState('ready');
      } catch {
        if (!cancelled) { setState('error'); }
      }
    }

    fetchConfig();
    return () => { cancelled = true; };
  }, [intentCode]);

  if (state === 'loading') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" role="status" aria-label="Loading" />
      </div>
    );
  }

  if (state === 'expired') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">This booking link has expired</h1>
        <p className="text-gray-600">This scheduling link is no longer active. Please contact us for assistance.</p>
      </div>
    );
  }

  if (state === 'exhausted') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">This booking link has reached its limit</h1>
        <p className="text-gray-600">This scheduling code has been used the maximum number of times.</p>
      </div>
    );
  }

  if (state === 'not_found') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">Booking not found</h1>
        <p className="text-gray-600">We couldn&apos;t find the scheduling page you&apos;re looking for.</p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">Something went wrong</h1>
        <p className="text-gray-600 mb-4">Please try again later.</p>
        <button
          onClick={() => window.location.reload()}
          className="min-h-[44px] px-6 py-3 bg-[#009263] text-white rounded-lg hover:bg-[#007a53] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const utm = config.campaignId ? {
    source: 'intent',
    medium: 'qr',
    campaign: config.campaignId,
    content: config.campaignChannelId || undefined,
  } : null;

  return (
    <div data-testid="calendar-booking-view">
      <SignupRequestView
        marketSlug={config.marketSlug}
        intentCode={intentCode}
        utm={utm}
        heading={config.heading || null}
        body={config.body || null}
        refId={refId}
      />
    </div>
  );
}
